import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { veiculosApi } from '../../api/endpoints'
import { Button, Spinner } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import {
  MetodoConsumo, METODO_CONSUMO_LABEL, OrigemModelo, TipoCombustivel, TIPO_COMBUSTIVEL_LABEL, TipoEnergia, TIPO_ENERGIA_LABEL,
  TipoVeiculo, TIPO_VEICULO_LABEL,
} from '../../lib/enums'
import { cn, formatMoney } from '../../lib/utils'
import { useRecurso } from '../plano/useRecurso'
import type { ModeloVeiculo, SalvarVeiculoRequest, ValorFipe, Veiculo } from '../../types'
import { energiaLabel, fonteFipe, hojeIso, usaEnergiaEletrica } from './veiculoUtils'

type Fonte = 'catalogo' | 'fipe' | 'manual'

interface Props {
  open: boolean
  onClose: () => void
  /** Com `veiculo` é edição: o modelo não muda. */
  veiculo?: Veiculo
  onSalvo?: (v: Veiculo) => void
}

export function VeiculoFormModal({ open, ...resto }: Props) {
  // O corpo só existe aberto, então o formulário recomeça a cada abertura.
  return open ? <Corpo {...resto} /> : null
}

const num = (s: string) => (s.trim() === '' ? undefined : Number(s.replace(',', '.')))
const COMBUSTIVEIS_ESCOLHA = [TipoCombustivel.Gasolina, TipoCombustivel.Etanol, TipoCombustivel.Diesel, TipoCombustivel.Gnv]

function Corpo({ onClose, veiculo, onSalvo }: Omit<Props, 'open'>) {
  const qc = useQueryClient()
  const toast = useToast()
  const editando = !!veiculo
  const fipeOk = useRecurso('veiculos.fipe').habilitado
  const permiteCarro = useRecurso('veiculos.tipo.carro').habilitado
  const permiteMoto = useRecurso('veiculos.tipo.moto').habilitado
  const permiteEletrico = useRecurso('veiculos.tipo.eletrico').habilitado
  const permiteHibrido = useRecurso('veiculos.tipo.hibrido').habilitado

  const [tipo, setTipo] = useState<TipoVeiculo>(TipoVeiculo.Carro)
  const [energia, setEnergia] = useState<TipoEnergia>(TipoEnergia.Combustao)
  const [fonte, setFonte] = useState<Fonte>('catalogo')
  const [selecionado, setSelecionado] = useState<ModeloVeiculo | null>(veiculo?.modelo ?? null)
  const [valorFipe, setValorFipe] = useState<ValorFipe | null>(null)

  // Catálogo
  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  useEffect(() => { const t = setTimeout(() => setBuscaAtiva(busca.trim()), 350); return () => clearTimeout(t) }, [busca])

  // FIPE
  const [marcaId, setMarcaId] = useState('')
  const [modeloFipeId, setModeloFipeId] = useState('')
  const [anoCodigo, setAnoCodigo] = useState('')

  // Manual
  const [manual, setManual] = useState({ marca: '', modelo: '', ano: '', versao: '', tanque: '', bateria: '' })
  const [combustiveis, setCombustiveis] = useState<TipoCombustivel[]>([TipoCombustivel.Gasolina, TipoCombustivel.Etanol])

  // Dados do veículo
  const [dados, setDados] = useState({
    apelido: veiculo?.apelido ?? '',
    km: veiculo ? String(veiculo.odometroInicial) : '',
    corId: veiculo?.corId ?? '',
    placa: veiculo?.placa ?? '',
    dataCompra: veiculo?.dataCompra?.slice(0, 10) ?? '',
    valorCompra: veiculo?.valorCompra != null ? String(veiculo.valorCompra) : '',
    tarifa: veiculo?.tarifaKwhCasa != null ? String(veiculo.tarifaKwhCasa) : '',
    metodo: (veiculo?.metodoPadrao ?? MetodoConsumo.TanqueCheio) as MetodoConsumo,
  })
  const set = (campo: keyof typeof dados, valor: string | number) => setDados((d) => ({ ...d, [campo]: valor }))

  const energiaModelo = fonte !== 'manual' && selecionado ? selecionado.tipoEnergia : energia

  const { data: cores = [] } = useQuery({ queryKey: ['veiculos', 'cores'], queryFn: veiculosApi.cores, staleTime: 3_600_000 })
  const { data: modelosCatalogo, isFetching: buscando } = useQuery({
    queryKey: ['veiculos', 'modelos', tipo, buscaAtiva],
    queryFn: () => veiculosApi.modelos({ busca: buscaAtiva || undefined, tipo }),
    enabled: !editando && fonte === 'catalogo' && (buscaAtiva.length === 0 || buscaAtiva.length >= 2),
  })
  const usaFipe = !editando && fonte === 'fipe' && fipeOk
  const { data: marcas, isLoading: carregandoMarcas } = useQuery({
    queryKey: ['veiculos', 'fipe', 'marcas', tipo], queryFn: () => veiculosApi.fipe.marcas(tipo), enabled: usaFipe,
  })
  const { data: modelosFipe, isLoading: carregandoModelos } = useQuery({
    queryKey: ['veiculos', 'fipe', 'modelos', tipo, marcaId], queryFn: () => veiculosApi.fipe.modelos(marcaId, tipo), enabled: usaFipe && !!marcaId,
  })
  const { data: anos, isLoading: carregandoAnos } = useQuery({
    queryKey: ['veiculos', 'fipe', 'anos', tipo, marcaId, modeloFipeId], queryFn: () => veiculosApi.fipe.anos(modeloFipeId, marcaId, tipo), enabled: usaFipe && !!marcaId && !!modeloFipeId,
  })
  const fipeIndisponivel = usaFipe && !!(marcas?.indisponivel || modelosFipe?.indisponivel || anos?.indisponivel)
  const avisoFipe = marcas?.aviso || modelosFipe?.aviso || anos?.aviso

  const criarModeloFipe = useMutation({
    mutationFn: (ano: string) => veiculosApi.criarModeloFipe({ tipo, marcaId, modeloId: modeloFipeId, anoCodigo: ano }),
    onSuccess: (r) => { setSelecionado(r.modelo); setValorFipe(r.valor ?? null); if (r.aviso) toast.error(r.aviso) },
    onError: (e: Error) => { setSelecionado(null); toast.error(e.message) },
  })

  function limparModelo() { setSelecionado(null); setValorFipe(null) }
  function trocarTipo(t: TipoVeiculo) { setTipo(t); limparModelo(); setMarcaId(''); setModeloFipeId(''); setAnoCodigo('') }
  function trocarEnergia(e: TipoEnergia) {
    setEnergia(e); limparModelo()
    setCombustiveis(e === TipoEnergia.Eletrico ? [] : [TipoCombustivel.Gasolina, TipoCombustivel.Etanol])
  }
  function trocarFonte(f: Fonte) { setFonte(f); limparModelo() }

  const manualValido = !!manual.marca.trim() && !!manual.modelo.trim() && Number(manual.ano) >= 1900
  const modeloPronto = editando || (fonte === 'manual' ? manualValido : !!selecionado)
  const podeSalvar = !!dados.apelido.trim() && dados.km.trim() !== '' && Number(dados.km.replace(',', '.')) >= 0 && modeloPronto

  const salvar = useMutation({
    mutationFn: async () => {
      let modeloId = veiculo?.modelo.id ?? selecionado?.id
      if (!editando && fonte === 'manual') {
        const comEletricidade = usaEnergiaEletrica(energia)
        const m = await veiculosApi.criarModeloManual({
          tipo, tipoEnergia: energia,
          marca: manual.marca.trim(), modelo: manual.modelo.trim(), anoModelo: Number(manual.ano), versao: manual.versao.trim() || null,
          combustiveisAceitos: [...(energia === TipoEnergia.Eletrico ? [] : combustiveis), ...(comEletricidade ? [TipoCombustivel.Eletricidade] : [])],
          capacidadeTanqueL: energia === TipoEnergia.Eletrico ? null : num(manual.tanque) ?? null,
          capacidadeBateriaKwh: comEletricidade ? num(manual.bateria) ?? null : null,
        })
        modeloId = m.id
      }
      const body: SalvarVeiculoRequest = {
        modeloVeiculoId: modeloId!,
        apelido: dados.apelido.trim(),
        odometroInicial: num(dados.km) ?? 0,
        corId: dados.corId || null,
        placa: dados.placa.trim().toUpperCase() || null,
        dataCompra: dados.dataCompra || null,
        valorCompra: num(dados.valorCompra) ?? null,
        tarifaKwhCasa: usaEnergiaEletrica(editando ? veiculo.modelo.tipoEnergia : energiaModelo) ? num(dados.tarifa) ?? null : null,
        metodoPadrao: dados.metodo,
      }
      return editando ? veiculosApi.atualizar(veiculo.id, body) : veiculosApi.criar(body)
    },
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: ['veiculos'] })
      toast.success(editando ? 'Veículo atualizado.' : 'Veículo cadastrado.')
      onSalvo?.(v)
      onClose()
    },
    // Limite, tipo ou plano barrados pela API chegam em português na mensagem.
    onError: (e: Error) => toast.error(e.message),
  })

  const bloqueioTipo = (t: TipoVeiculo) => (t === TipoVeiculo.Carro ? !permiteCarro : !permiteMoto)
  const bloqueioEnergia = (e: TipoEnergia) =>
    e === TipoEnergia.Eletrico ? !permiteEletrico : e === TipoEnergia.Hibrido || e === TipoEnergia.HibridoPlugin ? !permiteHibrido : false
  const mostraTarifa = usaEnergiaEletrica(editando ? veiculo.modelo.tipoEnergia : energiaModelo)

  return (
    <Modal
      open onClose={onClose} size="lg" title={editando ? 'Editar veículo' : 'Novo veículo'}
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-save-line" disabled={!podeSalvar} loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button></>}
    >
      <div className="space-y-5">
        {!editando && (
          <>
            <Grupo titulo="Tipo de veículo">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {([TipoVeiculo.Carro, TipoVeiculo.Moto] as const).map((t) => (
                  <Opcao key={t} ativo={tipo === t} bloqueado={bloqueioTipo(t)} onClick={() => trocarTipo(t)} icone={t === TipoVeiculo.Moto ? 'ri-motorbike-line' : 'ri-car-line'} rotulo={TIPO_VEICULO_LABEL[t]} />
                ))}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {([TipoEnergia.Combustao, TipoEnergia.Eletrico, TipoEnergia.Hibrido, TipoEnergia.HibridoPlugin] as const).map((e) => (
                  <Opcao key={e} ativo={energia === e} bloqueado={bloqueioEnergia(e)} onClick={() => trocarEnergia(e)} icone={e === TipoEnergia.Eletrico ? 'ri-flashlight-line' : e === TipoEnergia.Combustao ? 'ri-gas-station-line' : 'ri-leaf-line'} rotulo={TIPO_ENERGIA_LABEL[e]} />
                ))}
              </div>
              {(bloqueioEnergia(TipoEnergia.Eletrico) || bloqueioEnergia(TipoEnergia.Hibrido)) && (
                <p className="mt-2 text-xs text-muted"><i className="ri-lock-line" aria-hidden /> Os tipos com cadeado não estão no seu plano. <Link to="/plano" className="text-primary underline underline-offset-2">Ver planos</Link></p>
              )}
            </Grupo>

            <Grupo titulo="Modelo">
              <div className="mb-3 flex flex-wrap gap-2">
                <Aba ativo={fonte === 'catalogo'} onClick={() => trocarFonte('catalogo')}>Catálogo</Aba>
                <Aba ativo={fonte === 'fipe'} bloqueado={!fipeOk} onClick={() => trocarFonte('fipe')}>Tabela FIPE</Aba>
                <Aba ativo={fonte === 'manual'} onClick={() => trocarFonte('manual')}>Cadastro manual</Aba>
              </div>

              {fonte === 'catalogo' && (
                <div className="space-y-2">
                  <Input icon="ri-search-line" placeholder="Busque por marca ou modelo (mín. 2 letras)" value={busca} onChange={(e) => setBusca(e.target.value)} />
                  <div className="max-h-48 overflow-y-auto rounded border border-border/70">
                    {buscando ? <div className="flex justify-center p-4"><Spinner className="text-primary" /></div> : (() => {
                      const lista = (modelosCatalogo ?? []).filter((m) => m.tipoEnergia === energia)
                      if (lista.length === 0) return (
                        <p className="p-4 text-center text-13 text-muted">
                          {buscaAtiva.length < 2 ? 'Digite para buscar no catálogo.' : 'Nada encontrado.'}{' '}
                          <button type="button" className="text-primary underline underline-offset-2" onClick={() => trocarFonte(fipeOk ? 'fipe' : 'manual')}>{fipeOk ? 'Buscar pela FIPE' : 'Cadastrar manualmente'}</button>
                        </p>
                      )
                      return lista.map((m) => (
                        <button key={m.id} type="button" onClick={() => setSelecionado(m)} className={cn('block w-full px-3 py-2 text-left text-13 hover:bg-surface2', selecionado?.id === m.id && 'bg-primary/10 text-primary')}>
                          {m.marca} {m.modelo} {m.versao ?? ''} <span className="text-muted">· {m.anoModelo}{m.origem === OrigemModelo.Manual ? ' · manual' : ''}</span>
                        </button>
                      ))
                    })()}
                  </div>
                </div>
              )}

              {fonte === 'fipe' && (fipeIndisponivel ? (
                <div className="rounded bg-warning/10 px-3 py-2.5 text-13 text-warning">
                  {avisoFipe || 'A consulta à tabela FIPE está indisponível agora.'}{' '}
                  <button type="button" className="underline underline-offset-2" onClick={() => trocarFonte('manual')}>Cadastrar manualmente</button>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-3">
                  <Select label="Marca" value={marcaId} disabled={carregandoMarcas} onChange={(e) => { setMarcaId(e.target.value); setModeloFipeId(''); setAnoCodigo(''); limparModelo() }}>
                    <option value="">{carregandoMarcas ? 'Carregando...' : 'Selecione'}</option>
                    {marcas?.itens.map((m) => <option key={m.codigo} value={m.codigo}>{m.nome}</option>)}
                  </Select>
                  <Select label="Modelo" value={modeloFipeId} disabled={!marcaId || carregandoModelos} onChange={(e) => { setModeloFipeId(e.target.value); setAnoCodigo(''); limparModelo() }}>
                    <option value="">{carregandoModelos ? 'Carregando...' : 'Selecione'}</option>
                    {modelosFipe?.itens.map((m) => <option key={m.codigo} value={m.codigo}>{m.nome}</option>)}
                  </Select>
                  <Select label="Ano" value={anoCodigo} disabled={!modeloFipeId || carregandoAnos || criarModeloFipe.isPending} onChange={(e) => { setAnoCodigo(e.target.value); limparModelo(); if (e.target.value) criarModeloFipe.mutate(e.target.value) }}>
                    <option value="">{carregandoAnos ? 'Carregando...' : 'Selecione'}</option>
                    {anos?.itens.map((a) => <option key={a.codigo} value={a.codigo}>{a.nome}</option>)}
                  </Select>
                </div>
              ))}

              {fonte === 'manual' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Marca *" value={manual.marca} onChange={(e) => setManual({ ...manual, marca: e.target.value })} />
                  <Input label="Modelo *" value={manual.modelo} onChange={(e) => setManual({ ...manual, modelo: e.target.value })} />
                  <Input label="Ano do modelo *" type="number" inputMode="numeric" value={manual.ano} onChange={(e) => setManual({ ...manual, ano: e.target.value })} />
                  <Input label="Versão" value={manual.versao} onChange={(e) => setManual({ ...manual, versao: e.target.value })} />
                  {energia !== TipoEnergia.Eletrico && (
                    <div className="sm:col-span-2">
                      <span className="form-label">Combustíveis aceitos</span>
                      <div className="flex flex-wrap gap-2">
                        {COMBUSTIVEIS_ESCOLHA.map((c) => (
                          <Aba key={c} ativo={combustiveis.includes(c)} onClick={() => setCombustiveis((l) => (l.includes(c) ? l.filter((x) => x !== c) : [...l, c]))}>{TIPO_COMBUSTIVEL_LABEL[c]}</Aba>
                        ))}
                      </div>
                    </div>
                  )}
                  {energia !== TipoEnergia.Eletrico && <Input label="Capacidade do tanque (L)" type="number" inputMode="decimal" value={manual.tanque} onChange={(e) => setManual({ ...manual, tanque: e.target.value })} />}
                  {usaEnergiaEletrica(energia) && <Input label="Capacidade da bateria (kWh)" type="number" inputMode="decimal" value={manual.bateria} onChange={(e) => setManual({ ...manual, bateria: e.target.value })} />}
                </div>
              )}

              {criarModeloFipe.isPending && <p className="mt-2 flex items-center gap-2 text-13 text-muted"><Spinner className="text-primary" /> Consultando a tabela FIPE...</p>}
              {fonte !== 'manual' && selecionado && <ModeloEscolhido modelo={selecionado} valor={fonte === 'fipe' ? valorFipe : null} />}
            </Grupo>
          </>
        )}

        {editando && <ModeloEscolhido modelo={veiculo.modelo} valor={null} />}

        <Grupo titulo="Dados do veículo">
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Apelido *" value={dados.apelido} maxLength={60} placeholder="Ex.: Onix da família" onChange={(e) => set('apelido', e.target.value)} />
            <Input label={editando ? 'Km inicial *' : 'Km atual *'} type="number" inputMode="numeric" min={0} value={dados.km} onChange={(e) => set('km', e.target.value)} />
            <Input label="Placa (opcional)" value={dados.placa} maxLength={8} placeholder="ABC1D23" onChange={(e) => set('placa', e.target.value.toUpperCase())} />
            <Select label="Padrão de abastecimento" value={dados.metodo} onChange={(e) => set('metodo', Number(e.target.value))}>
              {Object.values(MetodoConsumo).map((m) => <option key={m} value={m}>{METODO_CONSUMO_LABEL[m]}</option>)}
            </Select>
            <Input label="Data da compra" type="date" max={hojeIso()} value={dados.dataCompra} onChange={(e) => set('dataCompra', e.target.value)} />
            <Input label="Valor da compra (R$)" type="number" inputMode="decimal" min={0} step="0.01" value={dados.valorCompra} onChange={(e) => set('valorCompra', e.target.value)} />
            {mostraTarifa && (
              <Input label="Tarifa de casa (R$/kWh)" type="number" inputMode="decimal" min={0} step="0.01" value={dados.tarifa} onChange={(e) => set('tarifa', e.target.value)} />
            )}
          </div>
          <div className="mt-3">
            <span className="form-label">Cor</span>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => set('corId', '')} className={cn('rounded border px-2.5 py-1 text-xs', !dados.corId ? 'border-primary text-primary' : 'border-border text-muted hover:text-white')}>Nenhuma</button>
              {cores.map((c) => (
                <button key={c.id} type="button" title={c.nome} aria-label={c.nome} aria-pressed={dados.corId === c.id} onClick={() => set('corId', c.id)}
                  className={cn('h-7 w-7 rounded-full border-2 transition-transform', dados.corId === c.id ? 'scale-110 border-primary' : 'border-border')} style={{ background: c.hex }} />
              ))}
            </div>
          </div>
          <p className="mt-3 text-xs text-muted">A placa é só uma identificação sua: o Walleon nunca a consulta em nenhuma base.</p>
        </Grupo>
      </div>
    </Modal>
  )
}

function Grupo({ titulo, children }: { titulo: string; children: ReactNode }) {
  return <section><h6 className="mb-2 text-13 font-medium uppercase text-muted">{titulo}</h6>{children}</section>
}

function Opcao({ ativo, bloqueado, onClick, icone, rotulo }: { ativo: boolean; bloqueado: boolean; onClick: () => void; icone: string; rotulo: string }) {
  return (
    <button
      type="button" disabled={bloqueado} onClick={onClick} aria-pressed={ativo}
      className={cn('flex items-center justify-center gap-2 rounded border px-3 py-2.5 text-13 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        ativo ? 'border-primary bg-primary/10 text-primary' : 'border-border text-white hover:bg-surface2')}
    >
      <i className={bloqueado ? 'ri-lock-line' : icone} aria-hidden /> {rotulo}
    </button>
  )
}

function Aba({ ativo, bloqueado, onClick, children }: { ativo: boolean; bloqueado?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button" disabled={bloqueado} onClick={onClick} aria-pressed={ativo}
      className={cn('inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        ativo ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted hover:text-white')}
    >
      {bloqueado && <i className="ri-lock-line" aria-hidden />}{children}
    </button>
  )
}

function ModeloEscolhido({ modelo, valor }: { modelo: ModeloVeiculo; valor: ValorFipe | null }) {
  return (
    <div className="mt-3 rounded bg-surface2 px-3 py-2.5 text-13">
      <p className="font-medium text-white">{modelo.marca} {modelo.modelo} {modelo.versao ?? ''} · {modelo.anoModelo}</p>
      <p className="mt-0.5 text-xs text-muted">
        {energiaLabel(modelo.tipoEnergia)}
        {modelo.combustiveisAceitos.length > 0 && ` · ${modelo.combustiveisAceitos.map((c) => TIPO_COMBUSTIVEL_LABEL[c]).join(', ')}`}
        {modelo.capacidadeTanqueL ? ` · tanque ${modelo.capacidadeTanqueL} L` : ''}
        {modelo.capacidadeBateriaKwh ? ` · bateria ${modelo.capacidadeBateriaKwh} kWh` : ''}
      </p>
      {valor && (
        <p className="mt-2 text-white">
          Valor de referência <span className="font-semibold text-primary">{formatMoney(valor.valor)}</span>
          <span className="mt-0.5 block text-xs text-muted">{fonteFipe(valor)}</span>
        </p>
      )}
    </div>
  )
}
